<template>
  <div class="summary-container mt-2">
    <h4
      id="title-summary-wire"
      data-testid="title-summary-wire"
      class="title-summary"
    >
      {{ $t('addFundPage.creditCardPayment.textSummary') }}
    </h4>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div
        id="title-amount-wire"
        data-testid="title-amount-wire"
        class="font-14 text-placeholder"
      >
        {{ $t('balance.textAmount') }}
      </div>
      <strong
        v-if="amountValidate"
        id="current-amount-wire"
        data-testid="current-amount-wire"
      >
        {{ localStringFormat(amount) }} USD
      </strong>
      <strong v-else> -- </strong>
    </div>

    <div
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-wire-method"
        data-testid="title-wire-method"
        class="font-14 text-placeholder"
      >
        {{ $t('addFundPage.creditCardPayment.textPaymentMethod') }}
      </div>
      <div
        v-if="amount"
        id="wire-method-field"
        data-testid="wire-method-field"
        class="d-flex align-items-center"
      >
        <strong id="payment-method">{{ convertNamePayment(paymentMethod.type) }}</strong>
      </div>
      <strong v-else> -- </strong>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-1">
      <div
        id="title-wire-transfer-bank"
        data-testid="title-wire-transfer-bank"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.wireTransfer.wireTransferBank') }}
      </div>
      <div v-if="amount">
        <strong
          id="wire-transfer-bank"
          data-testid="wire-transfer-bank"
        >{{ paymentMethod.value }}</strong>
      </div>
      <strong v-else> -- </strong>
    </div>
    <div
      v-if="percentFee"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-service-fee-wire"
        data-testid="title-service-fee-wire"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textServiceCharge') }}
        <small
          id="percent-service-charge-wire-fee"
          data-testid="percent-service-charge-wire-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentFeeTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wire-service-charge-fee"
          data-testid="wire-service-charge-fee"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wire-service-charge-fee"
            data-testid="current-wire-service-charge-fee"
            class="text-primary"
          >{{ serviceCharge }} USD</strong>
        </div>
        <strong v-else> -- </strong>
      </div>
    </div>

    <div
      v-if="methodFee"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-wire-fee"
        data-testid="title-wire-fee"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.paypal.textPaymentFee', {payment:"wire"}) }}
        <small
          id="percent-wire-fee"
          data-testid="percent-wire-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${methodFeeTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wire-fee"
            data-testid="current-wire-fee"
            class="text-primary"
          >{{ wireFee }} USD</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <div
      v-if="taxRate"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-tax-fee-wire"
        data-testid="title-tax-fee-wire"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textTax') }}
        <small
          id="percent-tax-wire-fee"
          data-testid="percent-tax-wire-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentTaxTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wire-tax-fee"
          data-testid="wire-tax-fee"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wire-tax-fee"
            data-testid="current-wire-tax-fee"
            class="text-primary"
          >{{ tax }} USD</strong>
        </div>
        <strong v-else> -- </strong>
      </div>
    </div>

    <div
      v-if="hasAMId && tipMode"
      class="d-flex align-items-center justify-content-between"
    >
      <div
        id="title-tip-wire"
        data-testid="title-tip-wire"
        class="font-14 text-placeholder"
      >
        Tip
        <small
          v-if="currentTip > 0"
          id="percent-tip-wire-fee"
          data-testid="percent-tip-wire-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentTipTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          id="wire-tip-amount"
          data-testid="wire-tip-amount"
          class="d-flex align-items-center"
        >
          <strong
            id="current-wire-tip-amount"
            data-testid="current-wire-tip-amount"
            class="text-primary"
          >{{ amountTipTxt }} USD</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <hr class="hr-custom">
    <div class="d-flex align-items-center justify-content-between pb-3">
      <div
        id="title-total-amount-wire"
        data-testid="title-total-amount-wire"
        class="font-24 text-placeholder"
      >
        {{ $t('addFundPage.creditCardPayment.textTotalAmount') }}
      </div>
      <strong
        v-if="amountValidate"
        id="current-wire-total-amount"
        data-testid="current-wire-total-amount"
        class="font-24 total-amount"
      >
        {{ totalAmount }} USD
      </strong>
      <strong v-else> -- </strong>
    </div>
  </div>
</template>

<script>
import paymentMixin from '@/mixins/paymentMixin'
import generalConfigsMixin from '@/mixins/generalConfigsMixin'
import numberFormatMixin from '@/mixins/numberFormatMixin'

// plugins
import { createNamespacedHelpers } from 'vuex'
import envMixin from '@/mixins/envMixin'

const { mapGetters: mapGettersAuth } = createNamespacedHelpers('auth')

export default {
  mixins: [paymentMixin, generalConfigsMixin, numberFormatMixin, envMixin],

  props: {
    amount: {
      type: Number,
      required: true,
    },
    paymentMethod: {
      type: Object,
      required: true,
    },
    transfer: {
      type: Object,
      required: true,
    },
    amountTip: {
      type: Number,
      required: true,
    },
    currentTip: {
      type: Number,
      required: true,
    },
  },

  computed: {
    ...mapGettersAuth(['user']),

    isInTrial() {
      return this.user?.isInTrial
    },

    percentFee() {
      return this.user?.data?.plans?.transferFee?.transferWire || 0
    },

    percentFeeTxt() {
      return `${(this.percentFee * 100).toFixed(2)}%`
    },

    percentTaxTxt() {
      return `${(this.taxRate * 100).toFixed(2)}%`
    },

    methodFee() {
      return this.generalConfigs?.paymentGateFee?.transferWireGate || 0
    },

    methodFeeTxt() {
      return `${(this.methodFee * 100).toFixed(2)}%`
    },

    wireFee() {
      return this.amountFormat(this.methodFee * this.amount).toLocaleString('en-US')
    },

    tax() {
      // return parseFloat(this.truncDigits(this.taxRate * this.amount)).toLocaleString('en-US')
      return this.amountFormat(this.taxRate * this.amount).toLocaleString(
        'en-US',
      )
    },

    percentTipTxt() {
      return `${(this.currentTip).toFixed(2)}%`
    },

    amountTipTxt() {
      return this.amountFormat(this.amountTip).toLocaleString('en-US')
    },

    totalAmount() {
      const fee = this.amountFormat(this.percentFee * this.amount)
      const tax = this.amountFormat(this.taxRate * this.amount)
      const methodFee = this.amountFormat(this.methodFee * this.amount)
      // return parseFloat(this.truncDigits(this.amount + fee + tax)).toLocaleString('en-US')
      return (this.amount + fee + tax + methodFee + this.amountTip).toLocaleString('en-US')
    },

    serviceCharge() {
      // return parseFloat(this.truncDigits(this.percentFee * this.amount)).toLocaleString('en-US')
      return this.amountFormat(this.percentFee * this.amount).toLocaleString(
        'en-US',
      )
    },

    amountValidate() {
      return !!(this.amount >= this.minTopUp && this.amount <= this.maxAmount)
    },

    maxAmount() {
      return 100000
    },
  },
}
</script>

<style lang="scss" scoped>
.summary-container {
  padding: 20px;
  background-color: #F5F5F7;
  // border: 1px solid rgba(22, 33, 62, 0.1);
  border-radius: 16px;

  .title-summary{
    margin-bottom: 21px;
  }
}

.transfer {
  max-width: 125px;
}

.total-amount{
  color: #00B9FF;
}
</style>
